import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faClipboardList,
  faChartBar,
  faEdit,
  faTrash,
  faBullseye,
  faCheckCircle,
  faExclamationTriangle,
  faShieldAlt,
  faProjectDiagram,
  faMapMarkedAlt,
  faNetworkWired,
  faSpinner,
  faUser,
  faCalendarAlt,
  faClock,
  faPlus,
  faInfoCircle,
  faTimes,
  faQuestion,
  faBan,
  faHourglassHalf,
  faCrosshairs
} from '@fortawesome/free-solid-svg-icons';
import projectService from '../services/projectService';
import actionService from '../services/actionService';
import targetService from '../services/targetService';
import webTargetService from '../services/webTargetService';
import webFindingService from '../services/webFindingService';
import TargetList from '../components/targets/TargetList';
import ActionList from '../components/actions/ActionList';
import AttackPathVisualization from '../components/visualization/AttackPathVisualization';
import AttackChainVisualization from '../components/visualization/AttackChainVisualization';
import ActionDetailModal from '../components/common/ActionDetailModal';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, formatDate } = useLanguage();
  const { darkMode } = useTheme();
  const [project, setProject] = useState(null);
  const [actions, setActions] = useState([]);
  const [targets, setTargets] = useState([]);
  const [webTargets, setWebTargets] = useState([]);
  const [webFindings, setWebFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [selectedAction, setSelectedAction] = useState(null);
  const [selectedTactic, setSelectedTactic] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const projectData = await projectService.getProject(projectId);
        setProject(projectData);
        const actionsData = await actionService.getActionsByProject(projectId);
        setActions(Array.isArray(actionsData) ? actionsData : []);
        const targetsData = await targetService.getTargetsByProject(projectId);
        setTargets(Array.isArray(targetsData) ? targetsData : []);

        // Fetch web targets + findings for attack path (silent fail)
        try {
          const wtData = await webTargetService.getAll(projectId);
          const wts = Array.isArray(wtData) ? wtData : (wtData?.data || []);
          setWebTargets(wts);
          if (wts.length > 0) {
            const wfData = await webFindingService.getAll({ project: projectId });
            setWebFindings(Array.isArray(wfData) ? wfData : (wfData?.data || []));
          }
        } catch (e) { /* no web data */ }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching project data:', error);
        setError(t('errors.general'));
        setLoading(false);
      }
    };
    fetchData();
  }, [projectId]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  const handleDeleteProject = async () => {
    if (window.confirm(t('projects.deleteConfirm') + ' ' + t('projects.deleteWarning'))) {
      try {
        await projectService.deleteProject(projectId);
        alert(t('common.success'));
        navigate('/projects');
      } catch (error) {
        alert(t('errors.deleteFailed'));
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'planning': darkMode ? 'bg-yellow-900/30 text-yellow-300 border-yellow-700' : 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'in-progress': darkMode ? 'bg-blue-900/30 text-blue-300 border-blue-700' : 'bg-blue-100 text-blue-800 border-blue-200',
      'completed': darkMode ? 'bg-green-900/30 text-green-300 border-green-700' : 'bg-green-100 text-green-800 border-green-200',
      'archived': darkMode ? 'bg-gray-700 text-gray-300 border-gray-600' : 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[status] || (darkMode ? 'bg-gray-700 text-gray-300 border-gray-600' : 'bg-gray-100 text-gray-800 border-gray-200');
  };

  const getResultColor = (result) => {
    const colors = {
      'successful': darkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800',
      'detected': darkMode ? 'bg-yellow-900/30 text-yellow-300' : 'bg-yellow-100 text-yellow-800',
      'blocked': darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-800',
      'failed': darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800',
      'partial': darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-800',
      'unknown': darkMode ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-100 text-purple-800'
    };
    return colors[result] || (darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800');
  };

  const getResultIcon = (result) => {
    const icons = {
      'successful': faCheckCircle, 'detected': faExclamationTriangle, 'blocked': faBan,
      'failed': faTimes, 'partial': faHourglassHalf, 'unknown': faQuestion
    };
    return icons[result] || faQuestion;
  };

  const getResultDotColor = (result) => {
    return {
      'successful': 'bg-green-500', 'detected': 'bg-yellow-500', 'blocked': 'bg-red-500',
      'failed': 'bg-gray-400', 'partial': 'bg-blue-500'
    }[result] || 'bg-gray-400';
  };

  const getMitreStats = () => {
    const tacticCount = {};
    const tacticActions = {};
    const resultCount = { successful: 0, detected: 0, blocked: 0, failed: 0, partial: 0, unknown: 0 };
    actions.forEach(action => {
      if (action.mitreAttackTechnique?.tactic) {
        const tac = action.mitreAttackTechnique.tactic;
        tacticCount[tac] = (tacticCount[tac] || 0) + 1;
        if (!tacticActions[tac]) tacticActions[tac] = [];
        tacticActions[tac].push(action);
      }
      if (action.result) resultCount[action.result] = (resultCount[action.result] || 0) + 1;
    });
    return { tacticCount, tacticActions, resultCount };
  };

  const formatDuration = (startTimestamp, endTimestamp) => {
    if (!endTimestamp) return null;
    const diffMs = new Date(endTimestamp) - new Date(startTimestamp);
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return diffHours > 0 ? `${diffHours}h ${diffMinutes}m` : `${diffMinutes}m`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-4xl text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`border px-4 py-3 rounded ${darkMode ? 'bg-red-900/30 border-red-700 text-red-300' : 'bg-red-50 border-red-200 text-red-700'}`}>
        {error}
      </div>
    );
  }

  if (!project) {
    return (
      <div className={`border px-4 py-3 rounded ${darkMode ? 'bg-yellow-900/30 border-yellow-700 text-yellow-300' : 'bg-yellow-50 border-yellow-200 text-yellow-700'}`}>
        {t('errors.notFound')}
      </div>
    );
  }

  const { tacticCount, tacticActions, resultCount } = getMitreStats();

  // Card/surface class helpers
  const cardBg = darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white';
  const surfaceBg = darkMode ? 'bg-gray-700' : 'bg-gray-50';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-600';
  const textMuted = darkMode ? 'text-gray-500' : 'text-gray-500';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <Link to="/projects" className="text-blue-600 hover:text-blue-800 flex items-center">
            <FontAwesomeIcon icon={faArrowLeft} className="mr-1" />
            {t('common.back')} {t('nav.projects')}
          </Link>
          <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(project.status)}`}>
            {t(`projects.${project.status}`) || project.status.charAt(0).toUpperCase() + project.status.slice(1)}
          </span>
        </div>
        <h1 className={`text-3xl font-bold ${textPrimary}`}>{project.name}</h1>
        <p className={textSecondary}>
          <span className="font-medium">{t('projects.client')}:</span> {project.client}
        </p>
        <p className={`text-sm ${textMuted}`}>
          {t('users.createdAt')}: {formatDate(project.createdAt)} &bull;
          {t('projectDetail.lastUpdated')}: {formatDate(project.updatedAt)}
        </p>
        <div className="flex items-center gap-2 mt-4 flex-wrap">
          <Link
            to={`/projects/${projectId}/debrief`}
            className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors flex items-center whitespace-nowrap"
          >
            <FontAwesomeIcon icon={faClipboardList} className="mr-2" />
            {t('projectDetail.generateDebrief')}
          </Link>
          <Link
            to={`/projects/${projectId}/reports`}
            className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition-colors flex items-center whitespace-nowrap"
          >
            <FontAwesomeIcon icon={faChartBar} className="mr-2" />
            {t('projectDetail.viewReports')}
          </Link>
          <button
            onClick={() => navigate(`/projects/${projectId}/edit`)}
            className={`px-4 py-2 rounded-md transition-colors flex items-center whitespace-nowrap ${darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          >
            <FontAwesomeIcon icon={faEdit} className="mr-2" />
            {t('projects.editProject')}
          </button>
          <button
            onClick={handleDeleteProject}
            className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors flex items-center whitespace-nowrap"
          >
            <FontAwesomeIcon icon={faTrash} className="mr-2" />
            {t('common.delete')}
          </button>
        </div>
      </div>

      {/* Quick Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className={`rounded-lg shadow-md p-4 ${cardBg}`}>
          <div className="flex items-center">
            <FontAwesomeIcon icon={faCrosshairs} className="text-2xl mr-3 text-primary" />
            <div>
              <p className={`text-2xl font-bold ${textPrimary}`}>{actions.length}</p>
              <p className={`text-sm ${textSecondary}`}>{t('dashboard.totalActions')}</p>
            </div>
          </div>
        </div>
        <div className={`rounded-lg shadow-md p-4 ${cardBg}`}>
          <div className="flex items-center">
            <FontAwesomeIcon icon={faBullseye} className="text-2xl mr-3 text-orange-600" />
            <div>
              <p className="text-2xl font-bold text-orange-600">{targets.length}</p>
              <p className={`text-sm ${textSecondary}`}>{t('nav.targets')}</p>
            </div>
          </div>
        </div>
        <div className={`rounded-lg shadow-md p-4 ${cardBg}`}>
          <div className="flex items-center">
            <FontAwesomeIcon icon={faCheckCircle} className="text-2xl mr-3 text-green-600" />
            <div>
              <p className="text-2xl font-bold text-green-600">{resultCount.successful}</p>
              <p className={`text-sm ${textSecondary}`}>{t('actions.successful')}</p>
            </div>
          </div>
        </div>
        <div className={`rounded-lg shadow-md p-4 ${cardBg}`}>
          <div className="flex items-center">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-2xl mr-3 text-yellow-600" />
            <div>
              <p className="text-2xl font-bold text-yellow-600">{resultCount.detected}</p>
              <p className={`text-sm ${textSecondary}`}>{t('actions.detected')}</p>
            </div>
          </div>
        </div>
        <div className={`rounded-lg shadow-md p-4 ${cardBg}`}>
          <div className="flex items-center">
            <FontAwesomeIcon icon={faMapMarkedAlt} className="text-2xl mr-3 text-purple-600" />
            <div>
              <p className="text-2xl font-bold text-purple-600">{Object.keys(tacticCount).length}</p>
              <p className={`text-sm ${textSecondary}`}>{t('mitre.tactics')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className={`rounded-lg shadow-md overflow-hidden ${cardBg}`}>
        <div className={`border-b ${borderColor}`}>
          <nav className="flex">
            {[
              { key: 'overview', icon: faClipboardList, label: t('common.overview') },
              { key: 'targets', icon: faBullseye, label: `${t('nav.targets')} (${targets.length})` },
              { key: 'actions', icon: faCrosshairs, label: `${t('projectDetail.redTeamActions')} (${actions.length})` },
              { key: 'mitre', icon: faMapMarkedAlt, label: t('projectDetail.mitreMap') },
              { key: 'attackpath', icon: faNetworkWired, label: t('projectDetail.attackPathVisualization') }
            ].map(tab => (
              <button
                key={tab.key}
                className={`px-6 py-3 text-sm font-medium flex items-center ${
                  activeTab === tab.key
                    ? darkMode
                      ? 'border-b-2 border-blue-400 text-blue-400 bg-gray-700'
                      : 'border-b-2 border-blue-500 text-blue-600 bg-blue-50'
                    : darkMode
                      ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => setActiveTab(tab.key)}
              >
                <FontAwesomeIcon icon={tab.icon} className="mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <h3 className={`text-lg font-semibold mb-3 flex items-center ${textPrimary}`}>
                    <FontAwesomeIcon icon={faInfoCircle} className="mr-2 text-primary" />
                    {t('projectDetail.projectInformation')}
                  </h3>
                  <div className="space-y-3">
                    {[
                      { label: t('common.status'), value: (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(project.status)}`}>
                          {t(`projects.${project.status}`) || project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                        </span>
                      )},
                      { label: t('projects.startDate'), value: formatDate(project.startDate) },
                      ...(project.endDate ? [{ label: t('projects.endDate'), value: formatDate(project.endDate) }] : []),
                      { label: t('users.createdAt'), value: formatDate(project.createdAt) },
                      { label: t('projectDetail.lastUpdated'), value: formatDate(project.updatedAt) }
                    ].map((item, i) => (
                      <div key={i} className="flex items-center">
                        <span className={`w-32 text-sm font-medium ${textMuted}`}>{item.label}:</span>
                        <span className={textPrimary}>{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className={`text-lg font-semibold mb-3 flex items-center ${textPrimary}`}>
                    <FontAwesomeIcon icon={faUser} className="mr-2 text-primary" />
                    {t('projects.team')}
                  </h3>
                  {project.team && project.team.length > 0 ? (
                    <div className="space-y-2">
                      {project.team.map((member, index) => (
                        <div key={index} className={`flex items-center justify-between p-2 rounded ${surfaceBg}`}>
                          <span className={`font-medium ${textPrimary}`}>{member.name}</span>
                          <span className={`text-sm ${textSecondary}`}>{member.role}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className={textMuted}>{t('projectDetail.noTeamMembers')}</p>
                  )}
                </div>
              </div>

              <div className="mb-6">
                <h3 className={`text-lg font-semibold mb-3 ${textPrimary}`}>{t('common.description')}</h3>
                <div className={`p-4 rounded-lg ${surfaceBg}`}>
                  <p className={`whitespace-pre-line ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{project.description}</p>
                </div>
              </div>

              {/* Recent Actions Preview */}
              {actions.length > 0 && (
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className={`text-lg font-semibold ${textPrimary}`}>{t('projectDetail.recentActions')}</h3>
                    <button onClick={() => setActiveTab('actions')} className="text-blue-600 hover:text-blue-800 text-sm">
                      {t('projectDetail.viewAll')} →
                    </button>
                  </div>
                  <div className="space-y-2">
                    {actions.slice(0, 3).map((action) => (
                      <div
                        key={action._id}
                        className={`p-3 rounded border cursor-pointer transition-colors ${
                          darkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                        }`}
                        onClick={() => setSelectedAction(action)}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <h4 className={`font-medium ${textPrimary}`}>{action.title}</h4>
                            <p className={`text-sm ${textMuted}`}>
                              {new Date(action.startTimestamp).toLocaleDateString()} &bull; {action.operator}
                              {action.endTimestamp && (
                                <span className="ml-2 text-blue-600">
                                  <FontAwesomeIcon icon={faClock} className="mr-1" />
                                  {formatDuration(action.startTimestamp, action.endTimestamp)}
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            {action.mitreAttackTechnique && (
                              <span className={`px-2 py-1 text-xs rounded-full ${darkMode ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-100 text-purple-800'}`}>
                                {action.mitreAttackTechnique.id}
                              </span>
                            )}
                            <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center ${getResultColor(action.result)}`}>
                              <FontAwesomeIcon icon={getResultIcon(action.result)} className="mr-1" />
                              {t(`actions.${action.result}`) !== `actions.${action.result}` ? t(`actions.${action.result}`) : action.result}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Targets Tab */}
          {activeTab === 'targets' && (
            <TargetList projectId={projectId} actions={actions} />
          )}

          {/* Actions Tab */}
          {activeTab === 'actions' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h3 className={`text-lg font-semibold ${textPrimary}`}>{t('projectDetail.redTeamActionsLogs')}</h3>
                <button
                  onClick={() => navigate(`/projects/${projectId}/actions/new`)}
                  className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors flex items-center"
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-2" />
                  {t('projectDetail.logNewAction')}
                </button>
              </div>

              {actions.length === 0 ? (
                <div className={`text-center py-10 rounded-lg ${surfaceBg}`}>
                  <FontAwesomeIcon icon={faBullseye} className={`text-6xl mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                  <p className={`text-lg mb-4 ${textSecondary}`}>{t('actions.noActions')}</p>
                  <p className={`mb-6 ${textMuted}`}>{t('projectDetail.startLoggingOperations')}</p>
                  <button
                    onClick={() => navigate(`/projects/${projectId}/actions/new`)}
                    className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center mx-auto"
                  >
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                    {t('projectDetail.logFirstAction')}
                  </button>
                </div>
              ) : (
                <ActionList
                  actions={actions}
                  projectId={projectId}
                  targets={targets}
                  onActionDeleted={(deletedId) => {
                    setActions(actions.filter(a => a._id !== deletedId));
                  }}
                />
              )}
            </div>
          )}

          {/* MITRE ATT&CK Tab */}
          {activeTab === 'mitre' && (
            <div>
              <h3 className={`text-lg font-semibold mb-6 ${textPrimary}`}>{t('projectDetail.mitreFrameworkAnalysis')}</h3>

              {actions.length === 0 ? (
                <div className={`text-center py-10 rounded-lg ${surfaceBg}`}>
                  <FontAwesomeIcon icon={faMapMarkedAlt} className={`text-6xl mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                  <p className={`text-lg mb-4 ${textSecondary}`}>{t('projectDetail.noMitreData')}</p>
                  <p className={`mb-6 ${textMuted}`}>{t('projectDetail.logActionsForCoverage')}</p>
                  <button
                    onClick={() => navigate(`/projects/${projectId}/actions/new`)}
                    className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center mx-auto"
                  >
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                    {t('projectDetail.logActionWithMitre')}
                  </button>
                </div>
              ) : (
                <div>
                  {/* MITRE Tactics Coverage - Clickable cards */}
                  <div className="mb-8">
                    <h4 className={`text-md font-semibold mb-4 ${textPrimary}`}>{t('projectDetail.tacticsCoverage')}</h4>
                    {Object.keys(tacticCount).length === 0 ? (
                      <p className={textMuted}>{t('projectDetail.noTechniquesMapped')}</p>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {Object.entries(tacticCount).map(([tactic, count]) => (
                          <div
                            key={tactic}
                            className={`p-4 rounded-lg border cursor-pointer transition-all ${
                              selectedTactic === tactic
                                ? darkMode
                                  ? 'bg-purple-900/40 border-purple-500 ring-2 ring-purple-500'
                                  : 'bg-purple-100 border-purple-400 ring-2 ring-purple-400'
                                : darkMode
                                  ? 'bg-gray-700 border-gray-600 hover:border-purple-500'
                                  : 'bg-purple-50 border-purple-200 hover:border-purple-400'
                            }`}
                            onClick={() => setSelectedTactic(selectedTactic === tactic ? null : tactic)}
                          >
                            <FontAwesomeIcon icon={faShieldAlt} className="text-2xl mb-2 text-purple-600" />
                            <div className={`text-sm font-medium ${darkMode ? 'text-purple-300' : 'text-purple-800'}`}>{tactic}</div>
                            <div className="text-lg font-bold text-purple-600">{count} technique{count !== 1 ? 's' : ''}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Kill Chain Visualization */}
                    {Object.keys(tacticCount).length > 0 && (
                      <div className="mt-6">
                        <AttackChainVisualization
                          actions={actions}
                          height={400}
                          projectId={projectId}
                          initialViewMode="killchain"
                          hideControls
                        />
                      </div>
                    )}

                    {/* Tactic drilldown - show actions for selected tactic */}
                    {selectedTactic && tacticActions[selectedTactic] && (
                      <div className={`mt-4 p-4 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-purple-200'}`}>
                        <h5 className={`font-semibold mb-3 flex items-center ${textPrimary}`}>
                          <FontAwesomeIcon icon={faShieldAlt} className="mr-2 text-purple-500" />
                          {selectedTactic} — {tacticActions[selectedTactic].length} {t('dashboard.totalActions').toLowerCase()}
                        </h5>
                        <div className="space-y-2">
                          {tacticActions[selectedTactic].map(action => (
                            <div
                              key={action._id}
                              className={`p-3 rounded border cursor-pointer transition-colors ${
                                darkMode ? 'bg-gray-800 border-gray-600 hover:bg-gray-600' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                              }`}
                              onClick={() => setSelectedAction(action)}
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <span className={`font-medium ${textPrimary}`}>{action.title}</span>
                                  <span className={`ml-2 text-sm ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                                    {action.mitreAttackTechnique?.id}
                                  </span>
                                </div>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getResultColor(action.result)}`}>
                                  <FontAwesomeIcon icon={getResultIcon(action.result)} className="mr-1" />
                                  {t(`actions.${action.result}`) !== `actions.${action.result}` ? t(`actions.${action.result}`) : action.result}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Results Breakdown */}
                  <div className="mb-8">
                    <h4 className={`text-md font-semibold mb-4 ${textPrimary}`}>{t('projectDetail.resultsAnalysis')}</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                      {Object.entries(resultCount).map(([result, count]) => (
                        count > 0 && (
                          <div key={result} className={`p-4 rounded-lg border ${getResultColor(result)} bg-opacity-20 ${darkMode ? 'border-gray-600' : ''}`}>
                            <FontAwesomeIcon icon={getResultIcon(result)} className="text-2xl mb-2" />
                            <div className="text-sm font-medium capitalize">
                              {t(`actions.${result}`) !== `actions.${result}` ? t(`actions.${result}`) : result}
                            </div>
                            <div className="text-lg font-bold">{count}</div>
                          </div>
                        )
                      ))}
                    </div>
                  </div>

                  {/* Action Timeline - Clickable items */}
                  <div>
                    <h4 className={`text-md font-semibold mb-4 ${textPrimary}`}>{t('projectDetail.actionTimeline')}</h4>
                    <div className="space-y-3">
                      {actions
                        .sort((a, b) => new Date(a.startTimestamp) - new Date(b.startTimestamp))
                        .map((action) => (
                          <div
                            key={action._id}
                            className={`flex items-center space-x-4 p-3 rounded-lg border cursor-pointer transition-colors ${
                              darkMode
                                ? 'bg-gray-700 border-gray-600 hover:bg-gray-600'
                                : 'bg-white border-gray-200 hover:bg-gray-50'
                            }`}
                            onClick={() => setSelectedAction(action)}
                          >
                            <div className="flex-shrink-0">
                              <div className={`w-3 h-3 rounded-full ${getResultDotColor(action.result)}`}></div>
                            </div>
                            <div className="flex-grow">
                              <div className="flex justify-between items-center">
                                <span className={`font-medium ${textPrimary}`}>{action.title}</span>
                                <div className={`text-sm ${textMuted}`}>
                                  <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
                                  {new Date(action.startTimestamp).toLocaleDateString()}
                                  {action.endTimestamp && (
                                    <span className="ml-2 text-blue-600">
                                      ({formatDuration(action.startTimestamp, action.endTimestamp)})
                                    </span>
                                  )}
                                </div>
                              </div>
                              {action.mitreAttackTechnique && (
                                <div className={`text-sm ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                                  {action.mitreAttackTechnique.id} - {action.mitreAttackTechnique.tactic}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Attack Path Visualization Tab */}
          {activeTab === 'attackpath' && (
            <div>
              <h3 className={`text-lg font-semibold mb-6 ${textPrimary}`}>{t('projectDetail.attackPathVisualization')}</h3>

              {actions.length === 0 ? (
                <div className={`text-center py-10 rounded-lg ${surfaceBg}`}>
                  <FontAwesomeIcon icon={faNetworkWired} className={`text-6xl mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                  <p className={`text-lg mb-4 ${textSecondary}`}>{t('projectDetail.noAttackPaths')}</p>
                  <p className={`mb-6 ${textMuted}`}>{t('projectDetail.logActionsToVisualize')}</p>
                  <button
                    onClick={() => navigate(`/projects/${projectId}/actions/new`)}
                    className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center mx-auto"
                  >
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                    {t('projectDetail.logFirstAction')}
                  </button>
                </div>
              ) : (
                <AttackPathVisualization
                  actions={actions}
                  targets={targets}
                  projectId={projectId}
                  webTargets={webTargets}
                  webFindings={webFindings}
                />
              )}
            </div>
          )}

        </div>
      </div>

      {/* Universal Action Detail Modal */}
      <ActionDetailModal
        action={selectedAction}
        isOpen={!!selectedAction}
        onClose={() => setSelectedAction(null)}
        projectId={projectId}
        onActionUpdated={(updated) => {
          setSelectedAction(updated);
          setActions(prev => prev.map(a => a._id === updated._id ? updated : a));
        }}
      />
    </div>
  );
};

export default ProjectDetail;
